import { useRef } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import {
  useExportableChart,
  svgToPngDataUrl
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'
import { buildBpmSvgForExport } from '@aytm/app/stats/page/question-views/bpm/lib/buildBpmSvgForExport'

import Chart from '@aytm/app/stats/page/question-views/bpm/components/quadrant/components/chart/Chart'

// The chart draws inside its container after mount, so the export polls for the svg rather than
// waiting on a readiness signal it does not emit, and then lets the load handler's own work -- the
// axis centering, the collision labels -- settle before reading it.
const POLL_MS = 100
const TIMEOUT_MS = 5000
const SETTLE_MS = 300

const waitForSvg = container =>
  new Promise((resolve, reject) => {
    const startedAt = performance.now()
    const tick = () => {
      const svg = container?.querySelector('.highcharts-container svg')
      if (svg) {
        setTimeout(() => resolve(svg), SETTLE_MS)
        return
      }
      if (performance.now() - startedAt > TIMEOUT_MS) {
        reject(new Error('bpm quadrant: chart svg never appeared'))
        return
      }
      setTimeout(tick, POLL_MS)
    }
    tick()
  })

// The quadrant plot, given the block's view. Selecting brands on the plot is a report param, so the
// selection is announced rather than held here.
//
// The export clones the drawn svg and parks the generic chart-container hook: serializing through
// the charting library would redraw, and the axis centering and collision labels are DOM work that
// a redraw loses.
const QuadrantChart = ({
  id,
  questionKey,
  questionVariants,
  batches,
  answer,
  scores,
  crossing,
  plots,
  title,
  dataLabels,
  xAxisLimits,
  yAxisLimits,
  colorTheme,
  chartLegendMode,
  features,
  quadrantBrandsValue,
  quadrantXAxisValue,
  quadrantYAxisValue,
  quadrantRadiusValue,
  quadrantColorValue,
  onModelChange,
  onChartReady
}) => {
  const containerRef = useRef(null)

  useExportableChart({
    deps: [true],
    prepareForExport: async () => {
      const svgNode = await waitForSvg(containerRef.current)
      const svgString = buildBpmSvgForExport(svgNode, 'q')

      return [await svgToPngDataUrl(svgString, svgNode.width.baseVal.value, svgNode.height.baseVal.value)]
    }
  })

  return (
    <Chart
      ref={containerRef}
      questionId={id}
      questionKey={questionKey}
      questionVariants={questionVariants}
      batches={batches}
      answer={answer}
      scores={scores}
      crossing={crossing}
      plots={plots}
      title={title}
      dataLabels={dataLabels}
      xAxisLimits={xAxisLimits}
      yAxisLimits={yAxisLimits}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      quadrantXAxisValue={quadrantXAxisValue}
      quadrantYAxisValue={quadrantYAxisValue}
      quadrantRadiusValue={quadrantRadiusValue}
      quadrantColorValue={quadrantColorValue}
      quadrantBrandsValue={quadrantBrandsValue}
      features={features}
      exportDelegated
      onBrandsChange={brands => onModelChange?.({ quadrantBrandsValue: brands })}
      onChartReady={onChartReady}
    />
  )
}

QuadrantChart.displayName = 'BpmQuadrantChart'

QuadrantChart.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  questionVariants: PropTypes.array,
  batches: PropTypes.array,
  answer: answerPropType,
  scores: PropTypes.array,
  crossing: PropTypes.object,
  plots: PropTypes.array,
  title: PropTypes.string,
  dataLabels: PropTypes.string,
  xAxisLimits: PropTypes.object,
  yAxisLimits: PropTypes.object,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  features: PropTypes.object,
  quadrantBrandsValue: PropTypes.array,
  quadrantXAxisValue: PropTypes.string,
  quadrantYAxisValue: PropTypes.string,
  quadrantRadiusValue: PropTypes.string,
  quadrantColorValue: PropTypes.string,
  onModelChange: PropTypes.func,
  onChartReady: PropTypes.func
}

export default QuadrantChart
